import {
  Process,
  ProcessRespondentSelectionCriterionType,
  KnownProcessType,
  ProcessParticipationApprovalRequirementType,
  ProcessReportSetting,
} from './../../../../models/process-models';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProcessService } from '../../../../services/process.service';
import { PmeEmplyeeFilter } from './pme-employee-filter.model';
import { NamedEntity } from '../../../../models/named-entity.model';
import { UserService } from '../../../../services/user.service';
import { TreeviewItem } from 'ngx-treeview';
import { MarsProjectSearch } from '../../../../models/mars-project-search.model';
import { CombinedEmployeeSearch } from '../../../../models/combined-employee-search.model';
import { ConfigService } from '../../../../services/config.service';
import { Grade } from '../../../../models/identity';
import { ProcessTypeFeatureFlags } from '../../../../models/process-type-feature-flags.enum';

@Component({
  selector: 'app-respondents-selection',
  templateUrl: './respondents-selection.component.html',
  styleUrls: ['./respondents-selection.component.scss'],
})
export class RespondentsSelectionComponent implements OnInit, OnDestroy {
  @Input() process: Process;

  processType = KnownProcessType;
  subscription: Subscription;
  selectionType = ProcessRespondentSelectionCriterionType;
  marsProjectSearch: MarsProjectSearch = { filters: [] };
  combinedSearchSettings: Array<CombinedEmployeeSearch> = [];
  isToggle: boolean = true;
  pmeEmployeeFilters: Array<PmeEmplyeeFilter> = [];
  grades: Array<Grade>;
  pmeStartDate = new Date();
  pmeEndDate = new Date();
  feature = ProcessTypeFeatureFlags;

  constructor(
    private processService: ProcessService,
    private userService: UserService,
    private configService: ConfigService
  ) {
    this.subscribeOnSave();
  }

  ngOnInit() {
    this.setDefaultValues();
    this.userService.getGrades().subscribe((r) => {
      this.grades = r;
      this.setSelectedValues();
    });
  }

  toggleSelectionValue(
    event,
    selectionType: ProcessRespondentSelectionCriterionType
  ) {
    if (event.currentTarget.checked) {
      this.setSelectionValue(
        selectionType,
        selectionType === ProcessRespondentSelectionCriterionType.TeamMinAmount
          ? '1'
          : '0'
      );
    } else {
      this.removeSelectionValue(selectionType);
    }
  }

  isSelectionValueSet(
    selectionType: ProcessRespondentSelectionCriterionType,
    processType: KnownProcessType = KnownProcessType._360
  ) {
    return this.process.respondentSelectionValues.some(
      (s) => s.selectionType === selectionType && s.processType === processType
    );
  }

  getSelectionValue(
    selectionType: ProcessRespondentSelectionCriterionType,
    processType: KnownProcessType = KnownProcessType._360
  ) {
    return this.process.respondentSelectionValues.find(
      (c) => c.processType === processType && c.selectionType === selectionType
    );
  }

  getSelectionValues(processType: KnownProcessType = KnownProcessType._360) {
    return this.process.respondentSelectionValues.filter(
      (c) => c.processType === processType
    );
  }

  removeSelectionValues(processType: KnownProcessType = KnownProcessType._360) {
    const values = this.getSelectionValues(processType);
    values.forEach((v) => {
      const index = this.process.respondentSelectionValues.indexOf(v);
      this.process.respondentSelectionValues.splice(index, 1);
    });
  }

  createPmeEmployeeFilter(): PmeEmplyeeFilter {
    return {
      selectorAppraiseeGrades: this.mapNamedArrayToNgxSelect(this.grades, []),
      selectorRespondentGrades: this.mapNamedArrayToNgxSelect(this.grades, []),
      appraiseeHours: 0,
      respondentHours: 0,
    };
  }

  removePmeEmployeeFilter(filter) {
    const index = this.pmeEmployeeFilters.indexOf(filter);
    this.pmeEmployeeFilters.splice(index, 1);
  }

  removeSelectionValue(
    selectionType: ProcessRespondentSelectionCriterionType,
    processType: KnownProcessType = KnownProcessType._360
  ) {
    const index = this.process.respondentSelectionValues.findIndex(
      (c) => c.processType === processType && c.selectionType === selectionType
    );
    if (index !== -1) {
      this.process.respondentSelectionValues.splice(index, 1);
    }
  }

  setSelectionValue(
    selectionType: ProcessRespondentSelectionCriterionType,
    value: string,
    processType: KnownProcessType = KnownProcessType._360
  ) {
    const existing = this.getSelectionValue(selectionType, processType);
    if (existing) {
      existing.value = value;
    } else {
      this.pushSelectionValue(selectionType, value, processType);
    }
  }

  getCombinedValue(
    selectionType: ProcessRespondentSelectionCriterionType
  ): number {
    const selectionValue = this.getSelectionValue(selectionType);
    return selectionValue ? +selectionValue.value : null;
  }

  isProcessTypeSet(processType: KnownProcessType) {
    return this.process.processKnownTypes.indexOf(processType) !== -1;
  }

  onFocusOut(event, selectionType: ProcessRespondentSelectionCriterionType) {
    const value = event.currentTarget.value;
    this.setSelectionValue(selectionType, value);
  }

  toggleProcessType(processType: KnownProcessType) {
    this.processService.toggleEnumSetting(
      this.process.processKnownTypes,
      processType
    );
    this.setCategoryChartDisabled();
  }

  toggle360() {
    this.toggleProcessType(KnownProcessType._360);
    this.processService.toggleEnumSetting(
      this.process.approvalRequirementTypes,
      ProcessParticipationApprovalRequirementType.ByAppraisee
    );
    this.setCategoryChartDisabled();
  }

  selectPmeAppraiseeGrade(
    filter: PmeEmplyeeFilter,
    selectedItems: Array<TreeviewItem>
  ) {
    filter.selectedAppraiseeIds = selectedItems.map((c) => c.value);
    this.filterRespondentGrades(filter, filter.selectedAppraiseeIds);
  }

  selectPmeRespondentGrade(
    filter: PmeEmplyeeFilter,
    selectedItems: Array<TreeviewItem>
  ) {
    filter.selectedRespondentIds = selectedItems.map((c) => c.value);
  }

  ngOnDestroy(): void {
    if (!this.subscription) {
      return;
    }
    this.subscription.unsubscribe();
  }

  private setCategoryChartDisabled() {
    if (
      this.processService.getIs360SetOnly(this.process) ||
      !this.process.reportSettings ||
      this.process.reportSettings.indexOf(
        ProcessReportSetting.ShowRespondentsCategoryChart
      ) === -1
    ) {
      return;
    }
    this.processService.toggleEnumSetting(
      this.process.reportSettings,
      ProcessReportSetting.ShowRespondentsCategoryChart
    );
  }

  private filterRespondentGrades(
    filter: PmeEmplyeeFilter,
    selectedGradeIds: Array<number>
  ) {
    let lowestGrade: Grade = { gradeLevel: Number.MAX_VALUE };
    this.grades.forEach((grade) => {
      if (
        selectedGradeIds.indexOf(grade.id) !== -1 &&
        grade.gradeLevel < lowestGrade.gradeLevel
      ) {
        lowestGrade = grade;
      }
    });
    const respondentGrades = this.grades.filter(
      (g) => g.gradeLevel < lowestGrade.gradeLevel
    );
    filter.selectorRespondentGrades = this.mapNamedArrayToNgxSelect(
      respondentGrades,
      []
    );
  }

  private setDefaultValues() {
    if (!this.process.respondentSelectionValues) {
      this.process.respondentSelectionValues = [];
    }
    if (!this.process.processKnownTypes) {
      this.process.processKnownTypes = [];
    }
    if (!this.process.minRespondentAmount) {
      this.process.minRespondentAmount = this.configService.configData.minRespondentAmount;
    }
  }

  private setSelectedValues() {
    this.setPmeSettings();
    this.setCombinedSettings();
  }

  private setCombinedSettings() {
    const combinedArray = this.getSelectionValues(KnownProcessType.Combined);
    if (!combinedArray) {
      return;
    }
    combinedArray.forEach((c) =>
      this.combinedSearchSettings.push(JSON.parse(c.value))
    );
  }

  private setPmeSettings() {
    const pme = this.getSelectionValue(
      ProcessRespondentSelectionCriterionType.MarsProject,
      KnownProcessType.PME
    );
    if (!pme) {
      return;
    }
    this.marsProjectSearch = JSON.parse(pme.value);
    this.pmeStartDate = this.processService.parseDate(
      this.marsProjectSearch.startDate
    );
    this.pmeEndDate = this.processService.parseDate(
      this.marsProjectSearch.endDate
    );
    this.setMarsProjectSearchFilters();
  }

  private setMarsProjectSearchFilters() {
    if (!this.marsProjectSearch.filters) {
      return;
    }
    this.marsProjectSearch.filters.forEach((f) =>
      this.pmeEmployeeFilters.push({
        appraiseeHours: f.appraiseeFilter.hours,
        selectedAppraiseeIds: f.appraiseeFilter.gradeIds,
        selectorAppraiseeGrades: this.mapNamedArrayToNgxSelect(
          this.grades,
          f.appraiseeFilter.gradeIds ? f.appraiseeFilter.gradeIds : []
        ),
        respondentHours: f.respondentFilter.hours,
        selectedRespondentIds: f.respondentFilter.gradeIds,
        selectorRespondentGrades: this.mapNamedArrayToNgxSelect(
          this.grades,
          f.respondentFilter.gradeIds ? f.respondentFilter.gradeIds : []
        ),
      })
    );
  }

  private onProcessSave() {
    this.mapMarsProjectSearch();
    this.mapCombinedSearchSettings();
    if (this.isProcessTypeSet(KnownProcessType._360)) {
      return;
    }
    this.removeSelectionValues(KnownProcessType._360);
  }

  private mapCombinedSearchSettings() {
    this.removeSelectionValues(KnownProcessType.Combined);
    if (!this.isProcessTypeSet(KnownProcessType.Combined)) {
      return;
    }
    this.combinedSearchSettings.forEach((s) =>
      this.pushSelectionValue(
        ProcessRespondentSelectionCriterionType.Combined,
        JSON.stringify(s),
        KnownProcessType.Combined
      )
    );
  }

  private pushSelectionValue(
    selectionType: ProcessRespondentSelectionCriterionType,
    value: string,
    processType: KnownProcessType
  ) {
    this.process.respondentSelectionValues.push({
      processType: processType,
      selectionType: selectionType,
      value: value,
    });
  }

  private mapMarsProjectSearch() {
    if (!this.isProcessTypeSet(KnownProcessType.PME)) {
      this.removeSelectionValue(
        ProcessRespondentSelectionCriterionType.MarsProject,
        KnownProcessType.PME
      );
      return;
    }
    this.marsProjectSearch.startDate = this.processService.convertDateToString(
      this.pmeStartDate
    );
    this.marsProjectSearch.endDate = this.processService.convertDateToString(
      this.pmeEndDate
    );
    this.marsProjectSearch.filters = [];
    this.pmeEmployeeFilters.forEach((filter) =>
      this.marsProjectSearch.filters.push({
        appraiseeFilter: {
          gradeIds: filter.selectedAppraiseeIds,
          hours: filter.appraiseeHours,
        },
        respondentFilter: {
          gradeIds: filter.selectedRespondentIds,
          hours: filter.respondentHours,
        },
      })
    );
    const jsonValue = JSON.stringify(this.marsProjectSearch);
    this.setSelectionValue(
      ProcessRespondentSelectionCriterionType.MarsProject,
      jsonValue,
      KnownProcessType.PME
    );
  }

  private mapNamedArrayToNgxSelect(
    array: Array<NamedEntity>,
    selectedIds: Array<number>
  ) {
    return array.map((x) =>
      Object.assign(
        new TreeviewItem({
          value: x.id,
          text: x.name,
          checked: selectedIds.indexOf(x.id) !== -1,
          children: [],
          collapsed: true,
        })
      )
    );
  }

  private subscribeOnSave() {
    if (this.subscription) {
      return;
    }
    this.subscription = this.processService.saveProcessClicked$.subscribe(() =>
      this.onProcessSave()
    );
  }
}
